/**
 * Exit-intent / inactivity / cart-abandonment email capture popup.
 *
 * Shows AT MOST ONCE per browser session, on whichever of these happens
 * first:
 * 1. Desktop exit-intent: mouse leaves the top of the viewport.
 * 2. 10s of no interaction (mousemove/scroll/click/touch) — works on any
 *    device, the only trigger available on mobile.
 * 3. Cart-add without checkout: adding something to cart re-confirms the
 *    same watchers (1) and (2) are armed on this page — in practice this
 *    is mostly a safety net, since (1)/(2) are already active the whole
 *    time the popup hasn't been shown yet.
 *
 * Once the popup has been shown for any reason — closed via the X, the
 * overlay, Escape, or successfully submitted — sessionStorage records
 * that and it will not show again for the rest of this browser session,
 * regardless of any later trigger condition. There is no recovery tab.
 *
 * The discount code itself (CALM15) is configured in Shopify as one-time
 * use per customer, independent of this session logic — so even a
 * customer who somehow saw it in two different sessions could still only
 * redeem it once at checkout.
 *
 * Auto-apply: on a successful submit, CALM15 is silently applied to the
 * customer's cart via Shopify's cart AJAX API (POST /cart/update.js with
 * a discount param) — no code entry needed at checkout. This works even
 * before anything is in the cart yet; it persists on the cart session and
 * is still there whenever they do add a product. Because a cart only ever
 * holds one code-type discount at a time, this also naturally replaces
 * any other code already applied (e.g. PURE10) — CALM15 always wins for
 * someone who has just subscribed via the popup.
 *
 * Cart-add detection: this store's cart uses Shopify's standard AJAX Cart
 * API under the hood (both the theme's own add-to-cart forms and the
 * AOV cart-drawer app ultimately call it), so rather than depending on a
 * theme- or app-specific custom event, this listens at the network layer —
 * wrapping fetch and XMLHttpRequest to catch any successful POST to
 * /cart/add(.js).
 *
 * Submits through Shopify's native customer/newsletter mechanism: the
 * form is a real {% form 'customer' %} (see snippets/exit-intent-popup.liquid)
 * with contact[tags]=newsletter, the same pattern snippets/newsletter-form.liquid
 * uses. It submits as a real (non-scripted) native form post — this store
 * enforces Shopify's form spam-protection on every customer/contact form,
 * which rejects scripted fetch submissions ("Missing CAPTCHA token"), so a
 * genuine native submission is required. return_to: 'back' sends the
 * customer right back to the same page, and this script re-opens the
 * popup straight into the success/error state that the server already
 * rendered — the closest equivalent to an in-place update available
 * under that constraint.
 */
(function () {
  'use strict';

  // Never run any of this on checkout itself.
  if (/^\/checkout(\/|$)/.test(window.location.pathname)) {
    return;
  }

  var STATE_KEY = 'hp_exit_popup_state';
  var INACTIVITY_DELAY = 10000;
  var AUTO_APPLY_CODE = 'CALM15';

  var popup = document.querySelector('[data-exit-popup]');
  if (!popup) return;

  var overlay = popup.querySelector('[data-exit-popup-overlay]');
  var closeBtn = popup.querySelector('[data-exit-popup-close]');
  var form = document.getElementById('ExitIntentPopupForm');
  var emailInput = popup.querySelector('[data-exit-popup-email]');
  var submitBtn = popup.querySelector('[data-exit-popup-submit]');
  var submitLabel = popup.querySelector('[data-exit-popup-submit-label]');
  var spinner = popup.querySelector('[data-exit-popup-spinner]');
  var errorEl = popup.querySelector('[data-exit-popup-error]');
  var copyBtn = popup.querySelector('[data-exit-popup-copy]');
  var copyLabel = popup.querySelector('[data-exit-popup-copy-label]');

  // The server already re-opened the popup (post-submit reload) if either
  // of these is true: a successful subscribe, or a submission error.
  var postedSuccessfully = popup.getAttribute('data-exit-popup-posted-successfully') === 'true';
  var serverOpened =
    popup.classList.contains('m-exit-popup--active') ||
    (errorEl && !errorEl.hidden && errorEl.textContent.trim() !== '');

  function hasBeenShown() {
    try {
      return sessionStorage.getItem(STATE_KEY) === 'shown';
    } catch (e) {
      return false;
    }
  }

  function markShown() {
    try {
      sessionStorage.setItem(STATE_KEY, 'shown');
    } catch (e) {
      /* storage unavailable — worst case it can show more than once */
    }
  }

  function autoApplyDiscount(code) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code })
    }).catch(function () {
      /* best-effort — if this fails the customer still has the code
         visible on screen and can enter it manually at checkout */
    });
  }

  function openPopup() {
    if (hasBeenShown()) return;
    markShown();
    removeTriggers();

    popup.classList.add('m-exit-popup--active');
    popup.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('prevent-scroll');

    window.setTimeout(function () {
      if (emailInput) emailInput.focus();
    }, 300);
  }

  function closePopup() {
    popup.classList.remove('m-exit-popup--active');
    popup.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('prevent-scroll');
    // No recovery tab, no re-show — markShown() already happened in
    // openPopup(), so this popup simply will not appear again this
    // session, whether the customer submitted or just closed it.
  }

  /* ---------- triggers ---------- */

  var inactivityTimer = null;
  var triggersArmed = false;

  function resetInactivityTimer() {
    if (inactivityTimer) window.clearTimeout(inactivityTimer);
    inactivityTimer = window.setTimeout(openPopup, INACTIVITY_DELAY);
  }

  function handleExitIntent(e) {
    var toElement = e.relatedTarget || e.toElement;
    if (!toElement && e.clientY <= 0) {
      openPopup();
    }
  }

  var interactionEvents = ['mousemove', 'scroll', 'click', 'touchstart', 'keydown'];

  function removeTriggers() {
    triggersArmed = false;
    if (inactivityTimer) window.clearTimeout(inactivityTimer);
    interactionEvents.forEach(function (evt) {
      document.removeEventListener(evt, resetInactivityTimer);
    });
    document.removeEventListener('mouseout', handleExitIntent);
  }

  function initTriggers() {
    if (hasBeenShown() || triggersArmed) return;
    triggersArmed = true;

    resetInactivityTimer();
    interactionEvents.forEach(function (evt) {
      document.addEventListener(evt, resetInactivityTimer, { passive: true });
    });

    var hasFinePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
    if (hasFinePointer) {
      document.addEventListener('mouseout', handleExitIntent);
    }
  }

  /* ---------- cart-add detection (network layer, theme/app-agnostic) ---------- */

  function isCartAddUrl(url) {
    return typeof url === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url);
  }

  function onCartAdded() {
    // The item's in the cart but checkout hasn't happened — make sure the
    // same watchers that would already be running are in fact running.
    initTriggers();
  }

  (function watchCartAdd() {
    var originalFetch = window.fetch;
    if (typeof originalFetch === 'function') {
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url);
        var method = (init && init.method) || (input && input.method) || 'GET';
        var isAdd = isCartAddUrl(url) && String(method).toUpperCase() === 'POST';
        var result = originalFetch.apply(this, arguments);
        if (isAdd) {
          result.then(function (response) {
            if (response && response.ok) onCartAdded();
          }).catch(function () {
            /* network error — not an add-to-cart success, ignore */
          });
        }
        return result;
      };
    }

    var originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (isCartAddUrl(url) && String(method).toUpperCase() === 'POST') {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) onCartAdded();
        });
      }
      return originalOpen.apply(this, arguments);
    };
  })();

  /* ---------- initial page-load state ---------- */

  if (serverOpened) {
    // Popup already rendered open (success or error) after a native
    // form post-redirect — surface it and stop here, no timers needed.
    document.documentElement.classList.add('prevent-scroll');
    markShown();
    if (postedSuccessfully) {
      autoApplyDiscount(AUTO_APPLY_CODE);
    }
    // If it's an error re-open, the customer can still retry the same
    // form right there; closing it afterward still won't bring it back.
  } else if (!hasBeenShown()) {
    initTriggers();
  }

  /* ---------- close interactions ---------- */

  if (closeBtn) closeBtn.addEventListener('click', closePopup);
  if (overlay) overlay.addEventListener('click', closePopup);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && popup.classList.contains('m-exit-popup--active')) {
      closePopup();
    }
  });

  /* ---------- form submit ---------- */
  /* Native submission (no preventDefault on valid input) — required so the
     store's form spam-protection sees a genuine submit. Only block obviously
     invalid input client-side; a real submission handles the rest. */

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      var email = emailInput ? emailInput.value.trim() : '';
      if (!isValidEmail(email)) {
        e.preventDefault();
        showError('Enter a valid email address.');
        if (emailInput) emailInput.focus();
        return;
      }

      clearError();
      if (submitBtn) submitBtn.disabled = true;
      if (spinner) spinner.classList.add('animate-spin-show');
      if (submitLabel) submitLabel.style.opacity = '0.5';
      // No preventDefault: let the browser submit the form for real.
    });
  }

  /* ---------- copy to clipboard ---------- */

  if (copyBtn) {
    copyBtn.addEventListener('click', function () {
      var codeEl = popup.querySelector('[data-exit-popup-code]');
      var code = codeEl ? codeEl.textContent.trim() : '';
      if (!code) return;

      function onCopied() {
        copyBtn.classList.add('m-exit-popup__copy--copied');
        if (copyLabel) copyLabel.textContent = 'Copied';
        window.setTimeout(function () {
          copyBtn.classList.remove('m-exit-popup__copy--copied');
          if (copyLabel) copyLabel.textContent = 'Copy';
        }, 2000);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(onCopied).catch(function () {
          fallbackCopy(code, onCopied);
        });
      } else {
        fallbackCopy(code, onCopied);
      }
    });
  }

  function fallbackCopy(text, onDone) {
    var textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      onDone();
    } catch (e) {
      /* no-op */
    }
    document.body.removeChild(textarea);
  }
})();
